import { Box, Grid, Stack } from '@mui/material';
import { useEffect, useState } from 'react';
import {
  IAccessControlSettings,
  getAllDefaultAccessSettings,
  fetchWorkspaceAccessSettings,
  saveAccessControlSettings,
} from 'wisestep-api';
import { WsButton } from 'wisestep-base';

import { SettingsHeader } from '../../components/SettingsHeader';
import { WsBreadCrumbs } from '../../components/WsBreadCrumbs';
import { usePermissionContext } from '../../context';
import { warning, success } from '../../utils/NotificationMessages';
import { useNotificationStore } from '../../utils/NotificationStore';
import { TitleAndBreadCrumbs } from '../invoicing/helper/InvocieSettingsHelper';

import AddRoleAccessControl from './AddRoleAccessControl';
import PermissionGridDerived from './PermissionGridDerived';

export interface IComponentProps {
  module: string;
  url: string;
  titleAndBreadCrumbs: TitleAndBreadCrumbs;
}

const CustomListingAccess = ({ module, url, titleAndBreadCrumbs }: IComponentProps) => {
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [permissionData, setPermisionData] = useState<IAccessControlSettings[]>([]);

  const permissionContext = usePermissionContext();

  const expansionConfig = {
    VIEW: true,
    MANAGE: true,
    DELETE: true,
  };

  useEffect(() => {
    const loadData = async () => {
      if (permissionContext[module]) {
        setPermisionData(permissionContext[module].permissions);
      }
    };
    loadData();
  }, [module, permissionContext]);

  const loadDefaultData = async () => {
    if (permissionContext.isAuthorized) {
      const response = await getAllDefaultAccessSettings(url, module);
      setPermisionData(response.data);
    }
  };

  const saveData = async () => {
    try {
      const response = await saveAccessControlSettings(url, module, permissionData);
      if (response.status === 200) {
        setNotification(success('Permissions saved Successfully'));
        if (permissionContext.setAccessControlSettingsContext) {
          permissionContext.setAccessControlSettingsContext({
            [module]: { permissions: permissionData },
          });
        }
      }
    } catch (error) {
      setNotification(warning('Something Went Wrong , Please try again'));
    }
  };

  const cancelData = async () => {
    try {
      const workspaceAccessSettings = await fetchWorkspaceAccessSettings(url, module);
      if (workspaceAccessSettings.status === 200) {
        setPermisionData(workspaceAccessSettings.data);
        setNotification(success('Permissions are loaded Successfully'));
      } else {
        const response = await getAllDefaultAccessSettings(url, module);
        response.data ? setPermisionData(response.data) : setPermisionData([]);
      }
    } catch (error) {
      setNotification(warning('Something Went Wrong , Please try again'));
    }
  };

  const handleLatestData = (data: IAccessControlSettings[]) => {
    setPermisionData(data);
  };

  return (
    <Box borderRadius={1.5}>
      <WsBreadCrumbs breadCrumbsList={titleAndBreadCrumbs.BreadcrumbsList} />
      <SettingsHeader title={titleAndBreadCrumbs.Title} />
      <Grid container>
        <Grid item xs={12} marginTop={1} borderRadius={1.5}>
          <Stack p={1} spacing={2} direction="row" justifyContent="space-between">
            <AddRoleAccessControl></AddRoleAccessControl>
            <Stack p={1} spacing={2} direction="row" justifyContent="end">
              <WsButton variant="outlined" size="large" onClick={() => loadDefaultData()}>
                Reset
              </WsButton>
              <WsButton variant="contained" size="large" type="submit" onClick={() => cancelData()}>
                Cancel
              </WsButton>
              <WsButton variant="contained" size="large" type="submit" onClick={() => saveData()}>
                Save
              </WsButton>
            </Stack>
          </Stack>
          <Grid marginTop={2} p={1}>
            {permissionData.length > 0 && (
              <PermissionGridDerived
                accountPermissions={permissionData}
                handleChange={handleLatestData}
                expansionConfig={expansionConfig}
              />
            )}
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CustomListingAccess;
